import { distance, type GameData, type ParticipantId } from "./model.ts";
import type { GameSettings } from "./settings.ts";

/**
 * The energy storm.
 *
 * This replaces the four corner stations, which were the wrong shape for the
 * problem they were meant to solve. They opened every thirty seconds in all four
 * corners at once, handed out a full ring, and asked nothing of anyone — so the
 * periodic event in a game short of contact was the one thing reliably scattering
 * twenty players to four separate corners of the map.
 *
 * One place instead of four, somewhere new each time, announced before it opens so
 * everybody arrives together, and paid out over seconds rather than in a lump so
 * holding it is a fight rather than a touch. The warning is the important half:
 * without it whoever happens to be nearest collects for free, and the event
 * generates no contact at all.
 *
 * The knobs keep their old names. `stationInterval`, `stationActiveDuration`,
 * `stationRadius` and `stationBonus` still mean how often, how long, how big and
 * how much — so an operator's saved balance survives the change in geometry.
 */

export const STORM_COLOR = "#ffd84a";

/** Seconds of notice before it opens. Long enough to cross most of the arena. */
export const STORM_WARNING_SECONDS = 6;

/** How often a held storm hands over an orb, while the holder stays inside. */
export const STORM_DRIP_SECONDS = 0.85;

export type StormState = {
  /** Which storm this is, counting from the first. Drives its position. */
  cycle: number;
  /** Paying out right now. */
  active: boolean;
  /** Visible and counting down, but not yet paying. */
  warning: boolean;
  /** Seconds until it closes, when active. */
  activeRemaining: number;
  /** Seconds until it opens, when not. */
  nextActivation: number;
};

/**
 * Where storm `cycle` lands.
 *
 * Deterministic from the cycle number alone, so the server and every client agree
 * without it having to go on the wire — and so a client can draw the warning ring
 * in exactly the place the server will pay out.
 *
 * Kept to the middle of the arena rather than anywhere in it: a storm in a corner
 * can only be approached from one side, which hands it to whoever got there first
 * and makes the fight a queue.
 */
export function getStormCenter(cycle: number, width: number, height: number, radius: number) {
  // Two irrational multipliers, so consecutive storms are far apart and the
  // sequence does not visibly repeat.
  const u = (cycle * 0.6180339887 + 0.31) % 1;
  const v = (cycle * 0.4142135624 + 0.73) % 1;
  const marginX = Math.max(radius + 140, width * 0.22);
  const marginY = Math.max(radius + 140, height * 0.22);
  return {
    x: marginX + u * Math.max(1, width - marginX * 2),
    y: marginY + v * Math.max(1, height - marginY * 2),
  };
}

export function getStormState(elapsed: number, interval: number, activeDuration: number): StormState {
  const safeInterval = Math.max(1, interval);
  const now = Math.max(0, elapsed);
  const cycle = Math.floor(now / safeInterval);
  const phase = now - cycle * safeInterval;
  // The first cycle is the arena settling in; nothing opens before one full interval.
  const active = now >= safeInterval && phase < activeDuration;
  const nextActivation = active ? 0 : Math.max(0, safeInterval - phase);
  return {
    // While a storm is open it is *this* cycle's; the rest of the time the thing
    // worth pointing at is the next one, so the position advances with the warning.
    cycle: active ? cycle : cycle + 1,
    active,
    warning: !active && nextActivation <= STORM_WARNING_SECONDS,
    activeRemaining: active ? Math.max(0, activeDuration - phase) : 0,
    nextActivation,
  };
}

/** Reads the match clock without reconstructing it; an endless arena has no remainder. */
function clockOf(game: GameData, settings: GameSettings) {
  if (Number.isFinite(game.elapsed)) return game.elapsed;
  const duration = Number.isFinite(game.matchDuration) ? game.matchDuration : settings.matchDuration;
  return Math.max(0, duration - game.timeLeft);
}

/**
 * Advances the storm and pays whoever is standing in it.
 *
 * An orb at a time rather than the whole bonus at once. A lump sum is collected by
 * touching the edge and leaving; a drip has to be stood in, and standing still in
 * the one place everybody is heading for is the fight this whole mechanic exists
 * to cause. `stationBonus` still caps the total per player per storm, so holding it
 * from open to close is worth exactly what one visit used to be — just no longer
 * free.
 */
export function applyStorm(game: GameData, settings: GameSettings, dt: number) {
  if (!(game.stormTaken instanceof Map)) game.stormTaken = new Map();
  if (!(game.stormDrip instanceof Map)) game.stormDrip = new Map();
  if (!Number.isFinite(game.stationCycle)) game.stationCycle = -1;

  const state = getStormState(clockOf(game, settings), settings.stationInterval, settings.stationActiveDuration);
  if (game.stationCycle !== state.cycle) {
    game.stationCycle = state.cycle;
    game.stormTaken.clear();
    game.stormDrip.clear();
  }

  const collected: Array<{ owner: ParticipantId; added: number }> = [];
  if (!state.active) return { state, collected };

  const centre = getStormCenter(state.cycle, game.width, game.height, settings.stationRadius);
  for (const participant of game.participants) {
    if (!participant.alive) continue;
    const taken = game.stormTaken.get(participant.id) ?? 0;
    if (taken >= settings.stationBonus || participant.orbs >= settings.maxOrbs) continue;
    if (distance(participant.x, participant.y, centre.x, centre.y) > settings.stationRadius) {
      // Leaving resets the run-up to the next orb, so a core cannot edge in and out
      // of the zone and still collect on the same schedule as one holding it.
      game.stormDrip.delete(participant.id);
      continue;
    }

    const waited = (game.stormDrip.get(participant.id) ?? 0) + dt;
    if (waited < STORM_DRIP_SECONDS) {
      game.stormDrip.set(participant.id, waited);
      continue;
    }
    game.stormDrip.set(participant.id, waited - STORM_DRIP_SECONDS);
    const before = participant.orbs;
    participant.orbs = Math.min(settings.maxOrbs, participant.orbs + 1);
    const added = participant.orbs - before;
    if (added <= 0) continue;
    game.stormTaken.set(participant.id, taken + added);
    collected.push({ owner: participant.id, added });
  }
  return { state, collected };
}
